import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts'
import {
  Plus,
  Target,
  Dumbbell,
  Calendar,
  Settings,
  Edit3,
  Trash2,
  CheckCircle,
  Circle,
  TrendingUp,
  Award
} from 'lucide-react'
import { fitnessAPI } from '../../services/api'
import toast from 'react-hot-toast'

interface FitnessTrackerWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Exercise {
  id: number
  name: string
  category: string
}

interface WorkoutExercise {
  exercise_id: number
  exercise_name: string
  // Mirrors Exercise.category so we can render appropriate inputs (strength, cardio, etc.)
  category: string
  // Strength-based metrics
  sets?: number
  reps?: number
  weight?: number
  weight_unit?: string
  // Time-based metrics (primarily for cardio/endurance work)
  duration_seconds?: number
}

export default function FitnessTrackerWidget({ widget, isExpanded }: FitnessTrackerWidgetProps) {
  const [showAddWorkout, setShowAddWorkout] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [showAddExercise, setShowAddExercise] = useState(false)
  const [selectedView, setSelectedView] = useState<'overview' | 'workouts' | 'goals'>('overview')
  const [newWorkout, setNewWorkout] = useState({
    workout_date: new Date().toISOString().split('T')[0],
    duration_minutes: '',
    notes: '',
    exercises: [] as WorkoutExercise[]
  })
  const [newExercise, setNewExercise] = useState({
    name: '',
    category: 'strength'
  })
  const [exerciseSearch, setExerciseSearch] = useState('')
  const [goalTarget, setGoalTarget] = useState(3)
  const [lastWeights, setLastWeights] = useState<Record<number, { weight: number, unit: string }>>({})

  const queryClient = useQueryClient()

  // Load last weights from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('fitness-last-weights')
    if (saved) {
      try {
        setLastWeights(JSON.parse(saved))
      } catch (e) {
        console.error('Failed to parse saved weights:', e)
      }
    }
  }, [])

  // Save weights to localStorage when updated
  const saveLastWeight = (exerciseId: number, weight: number, unit: string) => {
    const updated = { ...lastWeights, [exerciseId]: { weight, unit } }
    setLastWeights(updated)
    localStorage.setItem('fitness-last-weights', JSON.stringify(updated))
  }

  // Auto-populate exercises when opening workout modal
  const populateExercisesFromLibrary = () => {
    if (exercises.length === 0) return

    // Get all exercises from the library and populate with last weights
    const populatedExercises = exercises.map((exercise: Exercise) => {
      const lastWeightData = lastWeights[exercise.id]
      return {
        exercise_id: exercise.id,
        exercise_name: exercise.name,
        category: exercise.category,
        sets: exercise.category === 'strength' ? 3 : undefined,
        reps: exercise.category === 'strength' ? 10 : undefined,
        weight: lastWeightData?.weight || 0,
        weight_unit: lastWeightData?.unit || 'kg',
        duration_seconds: exercise.category === 'cardio' ? 20 * 60 : undefined // 20 minutes default for cardio
      }
    })

    setNewWorkout(prev => ({
      ...prev,
      exercises: populatedExercises
    }))
  }

  // Fetch data
  const { data: workouts = [] } = useQuery({
    queryKey: ['fitness-workouts'],
    queryFn: async () => {
      const { data } = await fitnessAPI.getWorkouts(20)
      return data
    },
  })

  const { data: exercises = [] } = useQuery({
    queryKey: ['fitness-exercises'],
    queryFn: async () => {
      console.log('FitnessTracker: Fetching exercises...')
      const { data } = await fitnessAPI.getExercises()
      console.log('FitnessTracker: Received exercises:', data)
      return data as Exercise[]
    },
  })

  const { data: goal } = useQuery({
    queryKey: ['fitness-goal'],
    queryFn: async () => {
      const { data } = await fitnessAPI.getGoal()
      setGoalTarget(data.weekly_target || 3)
      return data
    },
  })

  const { data: stats } = useQuery({
    queryKey: ['fitness-stats'],
    queryFn: async () => {
      const { data } = await fitnessAPI.getStats()
      return data
    },
  })

  // Prepare chart data
  const weeklyChartData = stats?.weekly_stats?.slice(-8).map((week: any) => ({
    week: new Date(week.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    workouts: week.count,
    target: week.target,
    metGoal: week.met_goal
  })) || []

  const handleCreateWorkout = async () => {
    // Filter out incomplete exercises
    const completeExercises = newWorkout.exercises.filter(exercise => {
      if (exercise.category === 'strength') {
        // For strength: must have sets > 0 AND reps > 0 (weight can be 0)
        return (exercise.sets && exercise.sets > 0) && (exercise.reps && exercise.reps > 0)
      } else {
        // For cardio: must have duration > 0
        return exercise.duration_seconds && exercise.duration_seconds > 0
      }
    })

    if (completeExercises.length === 0) {
      toast.error('Please complete at least one exercise with proper data')
      return
    }

    try {
      await fitnessAPI.createWorkout({
        workout_date: newWorkout.workout_date,
        duration_minutes: newWorkout.duration_minutes ? parseInt(newWorkout.duration_minutes) : null,
        notes: newWorkout.notes,
        exercises: completeExercises
      })

      queryClient.invalidateQueries({ queryKey: ['fitness-workouts'] })
      queryClient.invalidateQueries({ queryKey: ['fitness-stats'] })
      toast.success(`Workout logged with ${completeExercises.length} exercise${completeExercises.length !== 1 ? 's' : ''}!`)

      setShowAddWorkout(false)
      setNewWorkout({
        workout_date: new Date().toISOString().split('T')[0],
        duration_minutes: '',
        notes: '',
        exercises: []
      })
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create workout')
    }
  }

  const handleAddExerciseToWorkout = (exercise: Exercise) => {
    const isStrength = exercise.category === 'strength'

    const base: WorkoutExercise = {
      exercise_id: exercise.id,
      exercise_name: exercise.name,
      category: exercise.category,
    }

    // Get last saved weight for this exercise
    const lastWeightData = lastWeights[exercise.id]

    const strengthDefaults: Partial<WorkoutExercise> = {
      sets: 3,
      reps: 10,
      weight: lastWeightData?.weight || 0,
      weight_unit: lastWeightData?.unit || 'kg',
    }

    const cardioDefaults: Partial<WorkoutExercise> = {
      // default 20 minutes for convenience; user can adjust
      duration_seconds: 20 * 60,
    }

    setNewWorkout({
      ...newWorkout,
      exercises: [
        ...newWorkout.exercises,
        {
          ...base,
          ...(isStrength ? strengthDefaults : cardioDefaults),
        },
      ],
    })
  }

  const handleUpdateExercise = (index: number, field: string, value: any) => {
    const updatedExercises = [...newWorkout.exercises]
    const exercise = updatedExercises[index]

    // Save weight when it's updated
    if (field === 'weight' && exercise.exercise_id && exercise.weight_unit) {
      saveLastWeight(exercise.exercise_id, value, exercise.weight_unit)
    } else if (field === 'weight_unit' && exercise.exercise_id && exercise.weight) {
      saveLastWeight(exercise.exercise_id, exercise.weight, value)
    }

    updatedExercises[index] = { ...exercise, [field]: value }
    setNewWorkout({ ...newWorkout, exercises: updatedExercises })
  }

  const handleRemoveExercise = (index: number) => {
    setNewWorkout({
      ...newWorkout,
      exercises: newWorkout.exercises.filter((_, i) => i !== index)
    })
  }

  const handleAddExercise = async () => {
    try {
      console.log('FitnessTracker: Adding exercise:', newExercise.name, newExercise.category)
      const { data } = await fitnessAPI.addExercise(newExercise.name, newExercise.category)
      console.log('FitnessTracker: Add exercise response:', data)

      // Build exercise object from server response
      const newExerciseItem: Exercise | null = data?.exercise
        ? {
            id: data.exercise.id,
            name: data.exercise.name,
            category: data.exercise.category || newExercise.category
          }
        : data?.id
          ? { id: data.id, name: newExercise.name, category: newExercise.category }
          : null

      console.log('FitnessTracker: Built exercise item:', newExerciseItem)

      if (newExerciseItem) {
        // Optimistically update global exercises list so it shows up in settings/stats
        queryClient.setQueryData<Exercise[]>(
          ['fitness-exercises'],
          (old) => {
            const current = Array.isArray(old) ? [...old] : []
            console.log('FitnessTracker: Current exercises in cache:', current.length)
            if (!current.find(e => e.id === newExerciseItem.id)) {
              current.push(newExerciseItem)
              console.log('FitnessTracker: Added exercise to cache, new count:', current.length)
            }
            return current
          }
        )

        // Also add this exercise to the current workout so the count increases immediately
        console.log('FitnessTracker: Adding exercise to current workout')
        handleAddExerciseToWorkout(newExerciseItem)
        console.log('FitnessTracker: Current workout exercises count:', newWorkout.exercises.length)
      }

      // Also trigger a background refetch to stay in sync with server
      queryClient.invalidateQueries({ queryKey: ['fitness-exercises'] })

      // Show appropriate message based on response
      const message: string = typeof data?.message === 'string' ? data.message : ''
      const isDuplicate = message.toLowerCase().includes('already exists')
      toast.success(isDuplicate ? 'Exercise already in your list!' : 'Exercise added!')

      setShowAddExercise(false)
      setNewExercise({ name: '', category: 'strength' })
    } catch (error: any) {
      console.error('Add exercise error:', error.response?.data)
      toast.error(error.response?.data?.error || 'Failed to add exercise')
    }
  }

  const handleSetGoal = async () => {
    try {
      await fitnessAPI.setGoal(goalTarget)
      queryClient.invalidateQueries({ queryKey: ['fitness-goal'] })
      queryClient.invalidateQueries({ queryKey: ['fitness-stats'] })
      toast.success('Goal updated!')
    } catch (error: any) {
      toast.error('Failed to update goal')
    }
  }

  const handleDeleteWorkout = async (workoutId: number) => {
    if (!confirm('Are you sure you want to delete this workout?')) return

    try {
      await fitnessAPI.deleteWorkout(workoutId)
      queryClient.invalidateQueries({ queryKey: ['fitness-workouts'] })
      queryClient.invalidateQueries({ queryKey: ['fitness-stats'] })
      toast.success('Workout deleted!')
    } catch (error) {
      toast.error('Failed to delete workout')
    }
  }

  const getCurrentWeekProgress = () => {
    const currentWeek = weeklyChartData[weeklyChartData.length - 1]
    return currentWeek || { workouts: 0, target: goalTarget, metGoal: false }
  }

  const currentWeek = getCurrentWeekProgress()

  return (
    <div className="h-full flex flex-col">
      <div className="p-3 border-b border-gray-200 space-y-2">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>Fitness Tracker</h3>
            <div className="flex items-center gap-1 text-xs">
              <Target size={12} />
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {currentWeek.workouts}/{currentWeek.target} this week
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => {
                populateExercisesFromLibrary()
                setShowAddWorkout(true)
              }}
              className="p-1.5 hover:bg-gray-100 rounded transition"
              title="Log workout"
            >
              <Plus size={14} />
            </button>
            <button
              onClick={() => setShowSettings(true)}
              className="p-1.5 hover:bg-gray-100 rounded transition"
              title="Settings"
            >
              <Settings size={14} />
            </button>
          </div>
        </div>

        {/* View tabs */}
        <div className="flex gap-1">
          {['overview', 'workouts', 'goals'].map((view) => (
            <button
              key={view}
              onClick={() => {
                console.log('FitnessTracker: Switching to view:', view)
                setSelectedView(view as any)
              }}
              className={`px-2 py-1 text-xs rounded transition capitalize ${
                selectedView === view ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {view}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {isExpanded ? (
          <div className="h-full p-3 space-y-4">
            {(() => {
              console.log('FitnessTracker: Rendering expanded view, selectedView:', selectedView, 'isExpanded:', isExpanded)
              return null
            })()}
            {selectedView === 'overview' && (
              <>
                {/* Current week progress */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
                    <div className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>
                      {currentWeek.workouts}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      This Week
                    </div>
                  </div>
                  <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
                    <div className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                      {stats?.current_streak || 0}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      Day Streak
                    </div>
                  </div>
                  <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
                    <div className={`text-2xl font-bold ${currentWeek.metGoal ? 'text-green-600' : 'text-orange-600'}`}>
                      {currentWeek.metGoal ? '✓' : '○'}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      Goal Met
                    </div>
                  </div>
                </div>

                {/* Weekly chart */}
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis
                        dataKey="week"
                        stroke="var(--color-text-secondary)"
                        fontSize={10}
                      />
                      <YAxis
                        stroke="var(--color-text-secondary)"
                        fontSize={10}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--color-surface)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '6px'
                        }}
                      />
                      <Bar dataKey="workouts" fill="var(--color-primary)" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="target" fill="var(--color-secondary)" radius={[2, 2, 0, 0]} opacity={0.3} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Recent workouts */}
                <div>
                  <h4 className="font-medium text-sm mb-2" style={{ color: 'var(--color-text-primary)' }}>Recent Workouts</h4>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {workouts.slice(0, 5).map((workout: any) => (
                      <div
                        key={workout.id}
                        className="flex items-center justify-between p-2 rounded text-sm"
                        style={{ backgroundColor: 'var(--color-surface)' }}
                      >
                        <div>
                          <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                            {new Date(workout.workout_date).toLocaleDateString()}
                          </div>
                          <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                            {workout.exercise_count} exercises
                            {workout.duration_minutes && ` • ${workout.duration_minutes}min`}
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteWorkout(workout.id)}
                          className="p-1 hover:bg-red-100 text-red-600 rounded transition"
                          title="Delete workout"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {selectedView === 'workouts' && (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>Workout History</h4>
                  <button
                    onClick={() => {
                      populateExercisesFromLibrary()
                      setShowAddWorkout(true)
                    }}
                    className="px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 transition"
                  >
                    Log Workout
                  </button>
                </div>

                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {workouts.map((workout: any) => (
                    <div
                      key={workout.id}
                      className="p-3 rounded-lg border"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'var(--color-border)'
                      }}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                            {new Date(workout.workout_date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                          {workout.duration_minutes && (
                            <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                              {workout.duration_minutes} minutes
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteWorkout(workout.id)}
                          className="p-1 hover:bg-red-100 text-red-600 rounded transition"
                          title="Delete workout"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>

                      <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        {workout.exercise_count} exercises performed
                      </div>

                      {workout.notes && (
                        <div className="mt-2 text-xs italic" style={{ color: 'var(--color-text-secondary)' }}>
                          {workout.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedView === 'goals' && (
              <div className="space-y-4">
                <div className="text-center p-4 rounded-lg" style={{ backgroundColor: 'var(--color-surface)' }}>
                  <Target size={48} className="mx-auto mb-2" style={{ color: 'var(--color-primary)' }} />
                  <div className="text-2xl font-bold mb-1" style={{ color: 'var(--color-text-primary)' }}>
                    {goal?.weekly_target || 3}
                  </div>
                  <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    Workouts per week
                  </div>
                  <div className="mt-2 text-xs" style={{ color: currentWeek.metGoal ? 'var(--color-secondary)' : 'var(--color-text-secondary)' }}>
                    {currentWeek.metGoal ? '🎉 Goal achieved this week!' : `${currentWeek.target - currentWeek.workouts} more to reach goal`}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    Set Weekly Goal
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={goalTarget}
                      onChange={(e) => setGoalTarget(parseInt(e.target.value))}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      style={{
                        backgroundColor: 'var(--color-surface)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)'
                      }}
                    >
                      {[1,2,3,4,5,6,7].map(num => (
                        <option key={num} value={num}>{num} workout{num !== 1 ? 's' : ''}</option>
                      ))}
                    </select>
                    <button
                      onClick={handleSetGoal}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
                    >
                      Update
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-sm mb-2" style={{ color: 'var(--color-text-primary)' }}>Goal Progress</h4>
                  <div className="space-y-2">
                    {weeklyChartData.slice(-4).map((week: any, index: number) => (
                      <div key={index} className="flex items-center justify-between p-2 rounded text-sm" style={{ backgroundColor: 'var(--color-surface)' }}>
                        <span style={{ color: 'var(--color-text-primary)' }}>{week.week}</span>
                        <div className="flex items-center gap-2">
                          <span style={{ color: 'var(--color-text-secondary)' }}>
                            {week.workouts}/{week.target}
                          </span>
                          {week.metGoal ? (
                            <CheckCircle size={16} className="text-green-600" />
                          ) : (
                            <Circle size={16} className="text-gray-400" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Compact view */
          <div className="h-full p-2 flex flex-col justify-center items-center text-center">
            {(() => {
              console.log('FitnessTracker: Rendering compact view, selectedView:', selectedView, 'isExpanded:', isExpanded)
              return null
            })()}
            <Dumbbell size={32} className="mb-2" style={{ color: 'var(--color-primary)' }} />
            <div className="text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
              {currentWeek.workouts}/{currentWeek.target} this week
            </div>
            <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {stats?.current_streak || 0} day streak
            </div>
            {currentWeek.metGoal && (
              <Award size={16} className="mt-1 text-yellow-500" />
            )}
          </div>
        )}
      </div>

      {/* Add Workout Modal */}
      {showAddWorkout && (
        <div className="absolute inset-0 rounded-lg p-4 flex flex-col z-20 overflow-hidden" style={{ backgroundColor: 'var(--color-widget-bg)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Log Workout</h3>
            <button onClick={() => setShowAddWorkout(false)} className="p-1 hover:bg-gray-100 rounded">×</button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Date
                </label>
                <input
                  type="date"
                  value={newWorkout.workout_date}
                  onChange={(e) => setNewWorkout({...newWorkout, workout_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Duration (minutes)
                </label>
                <input
                  type="number"
                  value={newWorkout.duration_minutes}
                  onChange={(e) => setNewWorkout({...newWorkout, duration_minutes: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                  placeholder="45"
                />
              </div>
            </div>

            {/* Add exercises */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  Exercises ({newWorkout.exercises.filter(ex => {
                    if (ex.category === 'strength') {
                      return (ex.sets && ex.sets > 0) && (ex.reps && ex.reps > 0)
                    } else {
                      return ex.duration_seconds && ex.duration_seconds > 0
                    }
                  }).length}/{newWorkout.exercises.length})
                </label>
                <button
                  onClick={() => setShowAddExercise(true)}
                  className="text-xs px-2 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
                >
                  + Add Exercise
                </button>
              </div>

              <div className="space-y-2 max-h-40 overflow-y-auto">
                {newWorkout.exercises.map((exercise, index) => (
                  <div
                    key={index}
                    className="p-2 rounded border"
                    style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {exercise.exercise_name}
                        {exercise.category && (
                          <span className="ml-1 text-[10px] uppercase text-gray-400">
                            ({exercise.category})
                          </span>
                        )}
                      </span>
                      <button
                        onClick={() => handleRemoveExercise(index)}
                        className="p-1 hover:bg-red-100 text-red-600 rounded transition"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>

                    {exercise.category === 'strength' ? (
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        <div>
                          <label className="block mb-1" style={{ color: 'var(--color-text-secondary)' }}>Sets</label>
                          <input
                            type="number"
                            value={exercise.sets || ''}
                            onChange={(e) =>
                              handleUpdateExercise(index, 'sets', parseInt(e.target.value) || 0)
                            }
                            className="w-full px-2 py-1 border rounded"
                            style={{
                              backgroundColor: 'var(--color-widget-bg)',
                              borderColor: 'var(--color-border)',
                              color: 'var(--color-text-primary)',
                            }}
                          />
                        </div>
                        <div>
                          <label className="block mb-1" style={{ color: 'var(--color-text-secondary)' }}>Reps</label>
                          <input
                            type="number"
                            value={exercise.reps || ''}
                            onChange={(e) =>
                              handleUpdateExercise(index, 'reps', parseInt(e.target.value) || 0)
                            }
                            className="w-full px-2 py-1 border rounded"
                            style={{
                              backgroundColor: 'var(--color-widget-bg)',
                              borderColor: 'var(--color-border)',
                              color: 'var(--color-text-primary)',
                            }}
                          />
                        </div>
                        <div>
                          <label className="block mb-1" style={{ color: 'var(--color-text-secondary)' }}>Weight</label>
                          <input
                            type="number"
                            step="0.5"
                            value={exercise.weight || ''}
                            onChange={(e) =>
                              handleUpdateExercise(index, 'weight', parseFloat(e.target.value) || 0)
                            }
                            className="w-full px-2 py-1 border rounded"
                            style={{
                              backgroundColor: 'var(--color-widget-bg)',
                              borderColor: 'var(--color-border)',
                              color: 'var(--color-text-primary)',
                            }}
                          />
                        </div>
                        <div>
                          <label className="block mb-1" style={{ color: 'var(--color-text-secondary)' }}>Unit</label>
                          <select
                            value={exercise.weight_unit || 'kg'}
                            onChange={(e) => handleUpdateExercise(index, 'weight_unit', e.target.value)}
                            className="w-full px-2 py-1 border rounded"
                            style={{
                              backgroundColor: 'var(--color-widget-bg)',
                              borderColor: 'var(--color-border)',
                              color: 'var(--color-text-primary)',
                            }}
                          >
                            <option value="kg">kg</option>
                            <option value="lbs">lbs</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <label className="block mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                            Duration (minutes)
                          </label>
                          <input
                            type="number"
                            value={
                              exercise.duration_seconds
                                ? Math.round(exercise.duration_seconds / 60)
                                : ''
                            }
                            onChange={(e) =>
                              handleUpdateExercise(
                                index,
                                'duration_seconds',
                                (parseInt(e.target.value) || 0) * 60
                              )
                            }
                            className="w-full px-2 py-1 border rounded"
                            style={{
                              backgroundColor: 'var(--color-widget-bg)',
                              borderColor: 'var(--color-border)',
                              color: 'var(--color-text-primary)',
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                Notes (optional)
              </label>
              <textarea
                value={newWorkout.notes}
                onChange={(e) => setNewWorkout({...newWorkout, notes: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
                rows={2}
                placeholder="How did the workout go?"
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={handleCreateWorkout}
              disabled={newWorkout.exercises.length === 0}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Log Workout
            </button>
            <button
              onClick={() => setShowAddWorkout(false)}
              className="px-4 py-2 rounded-lg transition"
              style={{
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border)'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add Exercise Modal */}
      {showAddExercise && (
        <div className="absolute inset-0 rounded-lg p-4 flex flex-col z-20" style={{ backgroundColor: 'var(--color-widget-bg)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Add Exercise</h3>
            <button onClick={() => setShowAddExercise(false)} className="p-1 hover:bg-gray-100 rounded">×</button>
          </div>

          <div className="flex-1 space-y-4">
            {/* Existing exercise library */}
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                Your Exercises
              </label>
              <input
                type="text"
                value={exerciseSearch}
                onChange={(e) => setExerciseSearch(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg text-sm mb-2"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)'
                }}
                placeholder="Search your exercises..."
              />
              <div
                className="max-h-40 overflow-y-auto border rounded-lg divide-y"
                style={{
                  backgroundColor: 'var(--color-surface)',
                  borderColor: 'var(--color-border)'
                }}
              >
                {(() => {
                  const filteredExercises = exercises
                    .filter((ex: Exercise) =>
                      ex.name.toLowerCase().includes(exerciseSearch.toLowerCase())
                    )
                  console.log('FitnessTracker: Filtered exercises to display:', filteredExercises.length, 'out of', exercises.length)
                  return filteredExercises.map((ex: Exercise) => (
                    <button
                      key={ex.id}
                      type="button"
                      onClick={() => {
                        console.log('FitnessTracker: Adding existing exercise to workout:', ex.name)
                        handleAddExerciseToWorkout(ex)
                        toast.success(`Added ${ex.name} to this workout`)
                      }}
                      className="w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-100 rounded-none"
                    >
                      <span style={{ color: 'var(--color-text-primary)' }}>{ex.name}</span>
                      <span className="text-[10px] uppercase text-gray-400">
                        {ex.category || 'uncategorized'}
                      </span>
                    </button>
                  ))
                })()}
                {exercises.length === 0 && (
                  <div className="px-3 py-2 text-xs text-gray-500">
                    No saved exercises yet. Use the form below to add one.
                  </div>
                )}
              </div>
            </div>

            {/* Create new exercise */}
            <div className="pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  New Exercise Name
                </label>
                <input
                  type="text"
                  value={newExercise.name}
                  onChange={(e) => setNewExercise({ ...newExercise, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                  placeholder="e.g., Bench Press"
                />
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                  Category
                </label>
                <select
                  value={newExercise.category}
                  onChange={(e) => setNewExercise({ ...newExercise, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  style={{
                    backgroundColor: 'var(--color-surface)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                >
                  <option value="strength">Strength Training</option>
                  <option value="cardio">Cardio</option>
                  <option value="flexibility">Flexibility</option>
                  <option value="sports">Sports</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={handleAddExercise}
              disabled={!newExercise.name.trim()}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Exercise
            </button>
            <button
              onClick={() => setShowAddExercise(false)}
              className="px-4 py-2 rounded-lg transition"
              style={{
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-border)'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="absolute inset-0 rounded-lg p-4 flex flex-col z-20" style={{ backgroundColor: 'var(--color-widget-bg)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Fitness Settings</h3>
            <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-gray-100 rounded">×</button>
          </div>

          <div className="flex-1 space-y-4">
            <div>
              <h4 className="font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>Quick Stats</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Total Workouts</div>
                  <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{stats?.total_stats?.total_workouts || 0}</div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Current Streak</div>
                  <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{stats?.current_streak || 0} days</div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Avg Duration</div>
                  <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {stats?.total_stats?.avg_duration ? `${Math.round(stats.total_stats.avg_duration)}min` : 'N/A'}
                  </div>
                </div>
                <div>
                  <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Exercises</div>
                  <div className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{exercises.length}</div>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowSettings(false)}
            className="mt-4 w-full px-4 py-2 rounded-lg hover:bg-gray-300 transition"
            style={{
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-primary)'
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  )
}
